// shell/components/table/columns/icon-toggle-column.ts

import { IconToggleColumnType } from '../types';
import { ColumnRenderer, CellRenderContext, CellUpdateType, CellRenderResult } from './column-base';

export class IconToggleColumnRenderer<T = unknown> extends ColumnRenderer<T> {
    readonly supportsGrid = true;
    readonly supportsTable = true;

    constructor(private config: IconToggleColumnType<T>) {
        super();
    }

    getGridCellClass(): string {
        return 'cs-column-toggle';
    }

    getTableCellClass(): string {
        return 'cs-column-toggle';
    }

    buildGridCell(ctx: CellRenderContext<T>, cellWrapper: HTMLElement): HTMLElement {
        return this.buildToggle(ctx);
    }

    buildTableCell(ctx: CellRenderContext<T>, cell: HTMLElement): HTMLElement {
        cell.classList.add('cs-column-toggle--table');
        return this.buildToggle(ctx);
    }

    private buildToggle(ctx: CellRenderContext<T>): HTMLElement {
        const isOn = Boolean(ctx.value);
        const iconName = isOn ? (this.config.iconOn ?? this.config.iconOff) : this.config.iconOff;

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'cs-column-toggle__btn';

        // Store item key AND current value as data attributes (avoids stale closure)
        const itemKey = this.config.getItemKey(ctx.item);
        button.dataset.itemKey = itemKey;
        button.dataset.value = String(isOn);

        // Accessibility
        button.setAttribute('aria-pressed', String(isOn));
        const ariaLabel = typeof this.config.ariaLabel === 'function'
            ? this.config.ariaLabel(ctx.item)
            : this.config.ariaLabel ?? ctx.column.header;
        button.setAttribute('aria-label', ariaLabel);

        if (isOn) {
            button.classList.add('cs-column-toggle__btn--active');
        }

        // Create icon (using iconify or similar)
        const icon = document.createElement('span');
        icon.className = 'iconify cs-column-toggle__icon';
        icon.dataset.icon = iconName;
        button.appendChild(icon);

        // Attach click handler
        this.attachClickHandler(button);

        return button;
    }

    /**
     * Create and attach click handler to button.
     * Extracted to single method to avoid code duplication between build and update.
     */
    private attachClickHandler(button: HTMLElement): void {
        const handleClick = (e: MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();

            const key = button.dataset.itemKey;
            if (!key) return;

            // Read current value from data attribute (source of truth)
            const currentValue = button.dataset.value === 'true';
            const newValue = !currentValue;

            // Update visual state immediately
            this.updateVisualState(button, newValue);

            // Notify callback
            this.config.onChange(key, newValue);
        };

        button.addEventListener('click', handleClick);

        // Register cleanup on the BUTTON element itself (not container)
        this.registerCleanup(button, () => {
            button.removeEventListener('click', handleClick);
        });
    }

    /**
     * Update only the visual state of the button (icon, classes, ARIA).
     * Does not attach event handlers.
     */
    private updateVisualState(button: HTMLElement, isOn: boolean): void {
        button.dataset.value = String(isOn);
        button.classList.toggle('cs-column-toggle__btn--active', isOn);
        button.setAttribute('aria-pressed', String(isOn));

        const icon = button.querySelector('.iconify') as HTMLElement;
        if (icon) {
            icon.dataset.icon = isOn
                ? (this.config.iconOn ?? this.config.iconOff)
                : this.config.iconOff;
        }
    }

    updateGridCell(ctx: CellRenderContext<T>, cellWrapper: HTMLElement, contentEl: HTMLElement | null, type: CellUpdateType): CellRenderResult {
        if (type === CellUpdateType.Full && contentEl) {
            // Clean up old button's event listener before rebinding
            this.cleanupElement(contentEl);

            // Update data attributes for new item (handles row recycling)
            const itemKey = this.config.getItemKey(ctx.item);
            contentEl.dataset.itemKey = itemKey;

            // Update visual state
            const isOn = Boolean(ctx.value);
            this.updateVisualState(contentEl, isOn);

            // Update aria-label if dynamic
            if (typeof this.config.ariaLabel === 'function') {
                contentEl.setAttribute('aria-label', this.config.ariaLabel(ctx.item));
            }

            // Re-attach click handler
            this.attachClickHandler(contentEl);
        }
        return { contentElement: contentEl };
    }

    updateTableCell(ctx: CellRenderContext<T>, cell: HTMLElement, contentEl: HTMLElement | null, type: CellUpdateType): CellRenderResult {
        // Same logic as grid cell update
        return this.updateGridCell(ctx, cell, contentEl, type);
    }
}
